<template>
  <modal-wrap
    :id="modalName"
    :name="modalName"
    :hide-footer="true"
    header-class="border-0 fix-error-header"
    body-class="integrations-modal"
    size="xl"
  >
    <template #modal-title>
      <h4 class="mb-0">Item History</h4>
    </template>
    <template #default>
      <easy-data-table
        ref="dataTable"
        v-model:server-options="serverOptions"
        table-class-name="vue3-easy-data-table table-shadow no-overflow"
        :server-items-length="serverItemsLength"
        :loading="loading"
        :headers="headers"
        :items="items"
        :prevent-context-menu-row="false"
        :fixed-header="false"
        no-hover
        alternating
        hide-footer
        @update:server-options="updateServerOptions"
      >
        <template #loading>
          <b-spinner class="mt-5" variant="success" />
        </template>
        <template #no-data>
          <div style="text-align: center">No data available</div>
        </template>
        <template #item="{ column, item }: { column: string; item: HistoryItem }">
          <span v-if="column === 'eventDate'">
            <RowDate :date="item.eventDate" />
          </span>
          <span v-else-if="column === 'eventName'">
            {{ item.eventName }}
          </span>
          <span v-else-if="column === 'eventDetails'">
            {{ item.eventDetails }}
          </span>
        </template>
      </easy-data-table>

      <TablePagination
        v-model:server-options-ref="serverOptions"
        v-model:data-table-ref="dataTable"
        :server-items-length="serverItemsLength"
      />
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { HistoryItem } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, watch, ref, computed, type ComputedRef, type Ref } from 'vue'
import { ITEM_HISTORY_MODAL } from '@/const/ModalTypes'
import { registerModals } from '@/services/Modal'
import { useItemStore } from '@/store/item'
import { useVueNextModalStore } from '@/store/vueNextModal'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'ItemHistoryModal',
  components: {
    RowDate,
    TablePagination,
    EasyDataTable,
    BSpinner,
    ModalWrap,
  },
  setup() {
    const modalName = ITEM_HISTORY_MODAL
    const modalStore = useVueNextModalStore()
    const itemStore = useItemStore()
    const dataTable = ref()

    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 25,
    })
    const serverItemsLength: ComputedRef<number> = computed(() => itemStore.historyCount)
    const loading: ComputedRef<boolean> = computed(() => itemStore.historyLoading)
    const items: ComputedRef<HistoryItem[]> = computed(() => itemStore.historyItems)
    const itemId: ComputedRef<string | null> = computed(() => modalStore.modalPropsByName(modalName)?.itemId || null)

    const headers: Header[] = [
      { text: 'Date', value: 'eventDate' },
      { text: 'Event', value: 'eventName' },
      { text: 'Details', value: 'eventDetails' },
    ]

    const loadFromServer = () => {
      const id = itemId.value
      if (!id) {
        return
      }
      itemStore.fetchHistory({ id, filter: serverOptions.value })
    }

    const updateServerOptions = (options: ServerOptions) => {
      serverOptions.value = options
      loadFromServer()
    }

    watch(itemId, loadFromServer, { immediate: true })

    registerModals(modalName)

    return {
      items,
      headers,
      loading,
      dataTable,
      modalName,
      serverOptions,
      serverItemsLength,
      updateServerOptions,
    }
  },
})
</script>
